import { CopyOutlined, RightOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  Collapse,
  Input,
  message,
  Row,
  Select,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FaStarOfLife } from 'react-icons/fa6';
import { existingLinkedAccount, linkedAccountSchema } from '../../Utils/Schema';
import { yupResolver } from '@hookform/resolvers/yup';
import './razorpaySplitting.css';
import { useDispatch, useSelector } from 'react-redux';
import {
  addExistingLinkedAccountId,
  createLinkedAccount,
  getLinkedAccount,
} from '../../Actions/RazorpaySplittingAction';
import { is } from 'date-fns/locale';
import { getRestaurantById } from '../../Actions/RestaurantAction';

const LinkedAccount = ({ next, markStepComplete }) => {
  const { Panel } = Collapse;
  const dispatch = useDispatch();

  const {
    restaurant,
    isCreateLinkedAccountLoading,
    isGetLinkedAccountLoading,
    allLinkedAccountData,
    isAddExistingLinkedAccountIdLoading,
  } = useSelector((state) => {
    const {
      isCreateLinkedAccountLoading,
      isGetLinkedAccountLoading,
      allLinkedAccountData,
      isAddExistingLinkedAccountIdLoading,
    } = state.razorpaySplitting;
    const { restaurant } = state.restaurant;
    return {
      restaurant,
      isCreateLinkedAccountLoading,
      isGetLinkedAccountLoading,
      allLinkedAccountData,
      isAddExistingLinkedAccountIdLoading,
    };
  });

  const getInitialFormValues = (data, extraData = {}) => ({
    email: data?.email ?? '',
    phone: data?.phone ?? '',
    legal_business_name: data?.legal_business_name ?? '',
    contact_name: data?.contact_name ?? '',
    business_type: data?.business_type ?? '',
    subcategory: data?.profile?.subcategory ?? '',
    business_model: data?.profile?.business_model ?? '',
    street1: data?.profile?.addresses?.registered?.street1 ?? '',
    street2: data?.profile?.addresses?.registered?.street2 ?? '',
    city: data?.profile?.addresses?.registered?.city ?? '',
    state: data?.profile?.addresses?.registered?.state ?? '',
    postal_code: data?.profile?.addresses?.registered?.postal_code ?? '',
    operationstreet1: data?.profile?.addresses?.operation?.street1 ?? '',
    operationstreet2: data?.profile?.addresses?.operation?.street2 ?? '',
    operationcity: data?.profile?.addresses?.operation?.city ?? '',
    operationstate: data?.profile?.addresses?.operation?.state ?? '',
    operationpostalcode: data?.profile?.addresses?.operation?.postal_code ?? '',
    pan: data?.legal_info?.pan ?? '',
    gst: data?.legal_info?.gst ?? '',
    linked_account_id: extraData?.linked_account_id ?? '',
  });
  const [sameAsRegistered, setSameAsRegistered] = useState(false);
  const [hasExistingLinkedAccount, setHasExistingLinkedAccount] =
    useState(false);
  const disabledFormData =
    restaurant?.payment_splitting?.linked_account_id ||
    allLinkedAccountData?.id;
  const {
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    control,
    reset,
    setValue,
    clearErrors,
    getValues,
    watch,
  } = useForm({
    defaultValues: getInitialFormValues({}),
    resolver: yupResolver(
      hasExistingLinkedAccount ? existingLinkedAccount : linkedAccountSchema
    ),
  });

  useEffect(() => {
    if (allLinkedAccountData && restaurant) {
      reset(
        getInitialFormValues(allLinkedAccountData, {
          linked_account_id: restaurant?.payment_splitting?.linked_account_id,
        })
      );
    }
  }, [allLinkedAccountData, restaurant, reset]);

  const handleCheckboxChange = (e) => {
    setHasExistingLinkedAccount(e.target.checked);
  };

  useEffect(() => {
    if (sameAsRegistered) {
      const values = getValues();
      setValue('operationstreet1', values.street1 || '');
      setValue('operationstreet2', values.street2 || '');
      setValue('operationcity', values.city || '');
      setValue('operationstate', values.state || '');
      setValue('operationcountry', values.country || 'IN');
      setValue('operationpostalcode', values.postal_code || '');
    } else {
      setValue('operationstreet1', '');
      setValue('operationstreet2', '');
      setValue('operationcity', '');
      setValue('operationstate', '');
      setValue('operationcountry', 'IN');
      setValue('operationpostalcode', '');
    }
  }, [sameAsRegistered, getValues, setValue]);

  useEffect(() => {
    dispatch(getLinkedAccount());
  }, []);

  const removeUndefined = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(removeUndefined);
    } else if (obj !== null && typeof obj === 'object') {
      return Object.entries(obj).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = removeUndefined(value);
        }
        return acc;
      }, {});
    }
    return obj;
  };

  const handleCreateLinkedAccount = (data) => {
    const hasValidOperationAddress = !!data.operationstreet1;

    const transformedData = {
      linked_account: {
        email: data.email,
        phone: data.phone,
        type: 'route',
        reference_id: restaurant?.slug,
        legal_business_name: data.legal_business_name,
        business_type: data.business_type,
        contact_name: data.contact_name || '',
        profile: {
          category: 'food',
          subcategory: data.subcategory,
          business_model: data.business_model,
          addresses: {
            registered: {
              street1: data.street1,
              street2: data.street2,
              city: data.city,
              state: data.state,
              postal_code: data.postal_code,
              country: data.country,
            },
            ...(hasValidOperationAddress && {
              operation: {
                street1: data.operationstreet1,
                street2: data.operationstreet2,
                city: data.operationcity,
                state: data.operationstate,
                postal_code: data.operationpostalcode,
                country: data.operationcountry || 'IN',
              },
            }),
          },
        },
        legal_info: {
          pan: data.pan,
          gst: data.gst,
        },
      },
    };

    const cleanedData = removeUndefined(transformedData);
    if (hasExistingLinkedAccount) {
      dispatch(
        addExistingLinkedAccountId(
          data,
          successAddExistingAccount,
          failureAddExistingAccount
        )
      );
    } else {
      dispatch(
        createLinkedAccount(
          cleanedData,
          successCreateLinkedAccount,
          failureCreateLinkedAccount
        )
      );
    }
  };
  const successCreateLinkedAccount = () => {
    markStepComplete();
    dispatch(getLinkedAccount());
    setTimeout(() => {
      message.success('Linked account created successfully');
    }, 1000);
    // next();
  };
  const failureCreateLinkedAccount = (error) => {
    const errorMessage =
      error?.error?.error?.description ||
      error?.error?.description ||
      'Something went wrong.';

    setTimeout(() => {
      message.error(errorMessage);
    }, 1000);
  };

  const successAddExistingAccount = () => {
    setTimeout(() => {
      message.success('Linked account Added successfully');
    }, 1000);
    dispatch(getRestaurantById(restaurant?.id));
    dispatch(getLinkedAccount());
  };

  const failureAddExistingAccount = (error) => {
    console.log({ error });
  };

  const options = [
    { label: 'Online food ordering', value: 'online_food_ordering' },
    { label: 'Restaurant', value: 'restaurant' },
    { label: 'Food court', value: 'food_court' },
    { label: 'Catering', value: 'catering' },
    { label: 'Alcohol', value: 'alcohol' },
    {
      label: 'Restaurant search and booking',
      value: 'restaurant_search_and_booking',
    },
    { label: 'Dairy Products', value: 'dairy_products' },
    { label: 'Bakeries', value: 'bakeries' },
  ];

  const businessTypeOptions = [
    { label: 'Llp', value: 'llp' },
    { label: 'Ngo', value: 'ngo' },
    { label: 'Other', value: 'other' },
    { label: 'Individual', value: 'individual' },
    { label: 'Partnership', value: 'partnership' },
    { label: 'Proprietorship', value: 'proprietorship' },
    { label: 'Public Limited', value: 'public_limited' },
    { label: 'Private Limited', value: 'private_limited' },
    { label: 'Trust, Society', value: 'trust, society' },
    { label: 'Not Yet Registered', value: 'not_yet_registered' },
    { label: 'Educational Institutes', value: 'educational_institutes' },
  ];

  const selectedBusinessType = watch('business_type');

  return (
    <form onSubmit={handleSubmit(handleCreateLinkedAccount)}>
      <Spin
        spinning={
          isCreateLinkedAccountLoading ||
          isGetLinkedAccountLoading ||
          isAddExistingLinkedAccountIdLoading
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
          <Col>
            <Checkbox
              checked={hasExistingLinkedAccount}
              onChange={handleCheckboxChange}
            >
              <Typography className='formHeading'>
                Do you have Exsting Linked Account?
              </Typography>
            </Checkbox>
          </Col>
        </Row>
        {hasExistingLinkedAccount ? (
          <Col span={24}>
            <Row gutter={[10, 16]}>
              <Col span={24}>
                <Typography
                  className='formHeading'
                  style={{ marginBottom: '-15px' }}
                >
                  Enter Linked Account ID
                </Typography>
              </Col>
              <Col span={24}>
                <Controller
                  name='linked_account_id'
                  control={control}
                  className='inputLabel'
                  as={<Input size='large' />}
                />
                {errors.linked_account_id && (
                  <p className='errorMessage'>
                    {errors.linked_account_id.message}
                  </p>
                )}
              </Col>
              <Col span={24}>
                <Row justify='center'>
                  <Col span={6}>
                    <Button
                      className='detailsButton'
                      htmlType='submit'
                      size='large'
                    >
                      Save
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        ) : (
          <Col>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Typography className='formHeading'>
                  Create a Linked account
                </Typography>
              </Col>
              {(allLinkedAccountData?.id ||
                restaurant?.payment_splitting?.linked_account_id) && (
                <Col span={12}>
                  <Row justify='end'>
                    <Col>
                      <Typography className='formHeading'>
                        {allLinkedAccountData?.id ||
                          restaurant?.payment_splitting?.linked_account_id}
                        <Tooltip title='Copy ID'>
                          <CopyOutlined
                            style={{ marginLeft: 24, cursor: 'pointer' }}
                            onClick={() => {
                              navigator.clipboard.writeText(
                                allLinkedAccountData?.id
                              );
                              message.success('ID copied to clipboard!');
                            }}
                          />
                        </Tooltip>
                      </Typography>
                    </Col>
                  </Row>
                </Col>
              )}

              <Col>
                <Row gutter={[10, 16]}>
                  <Col span={24}>
                    <Collapse
                      defaultActiveKey={['1']}
                      expandIcon={({ isActive }) => (
                        <RightOutlined
                          rotate={isActive ? 90 : 270}
                          className='text-blue-500'
                        />
                      )}
                      expandIconPosition='end'
                    >
                      <Panel
                        header={
                          <div className='formHeading'>Account Information</div>
                        }
                        key='1'
                      >
                        <Row gutter={[16, 16]}>
                          <Col span={24}>
                            <Row gutter={[10, 16]}>
                              <Col span={24}>
                                <Typography
                                  className='formHeading'
                                  style={{ marginBottom: '-15px' }}
                                >
                                  Email
                                  <FaStarOfLife
                                    style={{
                                      fontSize: '7px',
                                      color: '#eb5757',
                                      position: 'absolute',
                                      top: '7%',
                                      marginLeft: '2px',
                                    }}
                                  />
                                </Typography>
                              </Col>
                              <Col span={24}>
                                <Controller
                                  name='email'
                                  control={control}
                                  className='inputLabel'
                                  as={
                                    <Input
                                      size='large'
                                      disabled={disabledFormData}
                                    />
                                  }
                                />
                                {errors.email && (
                                  <p className='errorMessage'>
                                    {errors.email.message}
                                  </p>
                                )}
                              </Col>
                            </Row>
                          </Col>

                          <Col span={24}>
                            <Row gutter={[10, 16]}>
                              <Col span={24}>
                                <Typography
                                  className='formHeading'
                                  style={{ marginBottom: '-15px' }}
                                >
                                  Phone number
                                  <FaStarOfLife
                                    style={{
                                      fontSize: '7px',
                                      color: '#eb5757',
                                      position: 'absolute',
                                      top: '7%',
                                      marginLeft: '2px',
                                    }}
                                  />
                                </Typography>
                              </Col>
                              <Col span={24}>
                                <Controller
                                  name='phone'
                                  control={control}
                                  className='inputLabel'
                                  as={
                                    <Input
                                      size='large'
                                      disabled={disabledFormData}
                                    />
                                  }
                                />
                                {errors.phone && (
                                  <p className='errorMessage'>
                                    {errors.phone.message}
                                  </p>
                                )}
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </Panel>
                    </Collapse>
                  </Col>
                  <Col span={24}>
                    <Collapse
                      defaultActiveKey={['1']}
                      expandIcon={({ isActive }) => (
                        <RightOutlined
                          rotate={isActive ? 90 : 270}
                          className='text-blue-500'
                        />
                      )}
                      expandIconPosition='end'
                    >
                      <Panel
                        header={
                          <div className='formHeading'>Business details</div>
                        }
                        key='1'
                      >
                        <Row gutter={[16, 16]}>
                          <Col span={24}>
                            <Row gutter={[10, 16]}>
                              <Col span={24}>
                                <Typography
                                  className='formHeading'
                                  style={{ marginBottom: '-15px' }}
                                >
                                  Legal Business name
                                  <FaStarOfLife
                                    style={{
                                      fontSize: '7px',
                                      color: '#eb5757',
                                      position: 'absolute',
                                      top: '7%',
                                      marginLeft: '2px',
                                    }}
                                  />
                                </Typography>
                              </Col>
                              <Col span={24}>
                                <Controller
                                  name='legal_business_name'
                                  control={control}
                                  className='inputLabel'
                                  as={
                                    <Input
                                      size='large'
                                      disabled={disabledFormData}
                                    />
                                  }
                                />
                                {errors.legal_business_name && (
                                  <p className='errorMessage'>
                                    {errors.legal_business_name.message}
                                  </p>
                                )}
                              </Col>
                            </Row>
                          </Col>
                          <Col span={24}>
                            <Row gutter={[10, 16]}>
                              <Col span={24}>
                                <Typography
                                  className='formHeading'
                                  style={{ marginBottom: '-15px' }}
                                >
                                  Contact name
                                  <FaStarOfLife
                                    style={{
                                      fontSize: '7px',
                                      color: '#eb5757',
                                      position: 'absolute',
                                      top: '7%',
                                      marginLeft: '2px',
                                    }}
                                  />
                                </Typography>
                              </Col>
                              <Col span={24}>
                                <Controller
                                  name='contact_name'
                                  control={control}
                                  className='inputLabel'
                                  as={
                                    <Input
                                      size='large'
                                      disabled={disabledFormData}
                                    />
                                  }
                                />
                                {errors.contact_name && (
                                  <p className='errorMessage'>
                                    {errors.contact_name.message}
                                  </p>
                                )}
                              </Col>
                            </Row>
                          </Col>
                          <Col span={24}>
                            <Row gutter={[10, 16]}>
                              <Col span={24}>
                                <Typography
                                  className='formHeading'
                                  style={{ marginBottom: '-15px' }}
                                >
                                  Business type
                                  <FaStarOfLife
                                    style={{
                                      fontSize: '7px',
                                      color: '#eb5757',
                                      position: 'absolute',
                                      top: '7%',
                                      marginLeft: '2px',
                                    }}
                                  />
                                </Typography>
                              </Col>
                              <Col span={24}>
                                <Controller
                                  name='business_type'
                                  control={control}
                                  style={{ width: '100%' }}
                                  className='formHeading'
                                  as={
                                    <Select
                                      options={businessTypeOptions}
                                      size='large'
                                      className='customSelect'
                                      disabled={disabledFormData}
                                    />
                                  }
                                />
                                <Typography
                                  className='formHeading'
                                  style={{ marginTop: '5px', opacity: '0.7' }}
                                >
                                  Note: For now please Select only Individual
                                  business type
                                </Typography>
                                {errors.business_type && (
                                  <p className='errorMessage'>
                                    {errors.business_type.message}
                                  </p>
                                )}
                              </Col>
                            </Row>
                          </Col>
                          <Col span={12}>
                            <Row gutter={[10, 16]}>
                              <Col span={24}>
                                <Typography
                                  className='formHeading'
                                  style={{ marginBottom: '-15px' }}
                                >
                                  Business Category
                                  <FaStarOfLife
                                    style={{
                                      fontSize: '7px',
                                      color: '#eb5757',
                                      position: 'absolute',
                                      top: '7%',
                                      marginLeft: '2px',
                                    }}
                                  />
                                </Typography>
                              </Col>
                              <Col span={24}>
                                <Controller
                                  name='category'
                                  control={control}
                                  style={{
                                    width: '100%',
                                    fontFamily: 'Circular-900',
                                  }}
                                  defaultValue='Food'
                                  as={<Select size='large' disabled />}
                                />
                              </Col>
                            </Row>
                          </Col>
                          <Col span={12}>
                            <Row gutter={[10, 16]}>
                              <Col span={24}>
                                <Typography
                                  className='formHeading'
                                  style={{ marginBottom: '-15px' }}
                                >
                                  Business Sub Category
                                  <FaStarOfLife
                                    style={{
                                      fontSize: '7px',
                                      color: '#eb5757',
                                      position: 'absolute',
                                      top: '7%',
                                      marginLeft: '2px',
                                    }}
                                  />
                                </Typography>
                              </Col>
                              <Col span={24}>
                                <Controller
                                  name='subcategory'
                                  control={control}
                                  style={{
                                    width: '100%',
                                    fontFamily: 'Circular-900',
                                  }}
                                  as={
                                    <Select
                                      size='large'
                                      options={options}
                                      dropdownStyle={{
                                        fontFamily: 'Circular-400',
                                      }}
                                      disabled={disabledFormData}
                                    />
                                  }
                                />
                                {errors.subcategory && (
                                  <p className='errorMessage'>
                                    {errors.subcategory.message}
                                  </p>
                                )}
                              </Col>
                            </Row>
                          </Col>
                          <Col span={24}>
                            <Row gutter={[10, 16]}>
                              <Col span={24}>
                                <Typography
                                  className='formHeading'
                                  style={{ marginBottom: '-15px' }}
                                >
                                  Business Model{' '}
                                  <FaStarOfLife
                                    style={{
                                      fontSize: '7px',
                                      color: '#eb5757',
                                      position: 'absolute',
                                      top: '7%',
                                      marginLeft: '2px',
                                    }}
                                  />
                                </Typography>
                              </Col>
                              <Col span={24}>
                                <Controller
                                  name='business_model'
                                  control={control}
                                  className='inputLabel'
                                  as={
                                    <TextArea
                                      size='large'
                                      disabled={disabledFormData}
                                    />
                                  }
                                />
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </Panel>
                    </Collapse>
                  </Col>
                  <Col span={24}>
                    <Collapse
                      defaultActiveKey={['1']}
                      expandIcon={({ isActive }) => (
                        <RightOutlined
                          rotate={isActive ? 90 : 270}
                          className='text-blue-500'
                        />
                      )}
                      expandIconPosition='end'
                    >
                      <Panel
                        header={
                          <div className='formHeading'>Business Address</div>
                        }
                        key='1'
                      >
                        <Row gutter={[16, 16]}>
                          <Col>
                            <Typography
                              className='formHeading'
                              style={{ paddingBottom: '10px' }}
                            >
                              {' '}
                              Registered Address{' '}
                              <FaStarOfLife
                                style={{
                                  fontSize: '7px',
                                  color: '#eb5757',
                                  position: 'absolute',
                                  top: '1%',
                                  marginLeft: '2px',
                                }}
                              />
                            </Typography>

                            <Row
                              gutter={[5, 16]}
                              style={{
                                border: '1px solid #ccc',
                                padding: '10px',
                              }}
                            >
                              <Col span={24}>
                                <Row gutter={[10, 16]}>
                                  <Col span={24}>
                                    <Typography
                                      className='formHeading'
                                      style={{ marginBottom: '-15px' }}
                                    >
                                      Address line 1
                                    </Typography>
                                  </Col>
                                  <Col span={24}>
                                    <Controller
                                      name='street1'
                                      control={control}
                                      className='inputLabel'
                                      as={
                                        <Input
                                          size='large'
                                          disabled={disabledFormData}
                                        />
                                      }
                                    />
                                    {errors.street1 && (
                                      <p className='errorMessage'>
                                        {errors.street1.message}
                                      </p>
                                    )}
                                  </Col>
                                </Row>
                              </Col>

                              <Col span={24}>
                                <Row gutter={[10, 16]}>
                                  <Col span={24}>
                                    <Typography
                                      className='formHeading'
                                      style={{ marginBottom: '-15px' }}
                                    >
                                      Address line 2
                                    </Typography>
                                  </Col>
                                  <Col span={24}>
                                    <Controller
                                      name='street2'
                                      control={control}
                                      className='inputLabel'
                                      as={
                                        <Input
                                          size='large'
                                          disabled={disabledFormData}
                                        />
                                      }
                                    />
                                    {errors.street2 && (
                                      <p className='errorMessage'>
                                        {errors.street2.message}
                                      </p>
                                    )}
                                  </Col>
                                </Row>
                              </Col>
                              <Col span={24}>
                                <Row gutter={[10, 16]}>
                                  <Col span={12}>
                                    <Row gutter={[10, 16]}>
                                      <Col span={24}>
                                        <Typography
                                          className='formHeading'
                                          style={{ marginBottom: '-15px' }}
                                        >
                                          City
                                        </Typography>
                                      </Col>
                                      <Col span={24}>
                                        <Controller
                                          name='city'
                                          control={control}
                                          className='inputLabel'
                                          as={
                                            <Input
                                              size='large'
                                              disabled={disabledFormData}
                                            />
                                          }
                                        />
                                        {errors.city && (
                                          <p className='errorMessage'>
                                            {errors.city.message}
                                          </p>
                                        )}
                                      </Col>
                                    </Row>
                                  </Col>
                                  <Col span={12}>
                                    <Row gutter={[10, 16]}>
                                      <Col span={24}>
                                        <Typography
                                          className='formHeading'
                                          style={{ marginBottom: '-15px' }}
                                        >
                                          State
                                        </Typography>
                                      </Col>
                                      <Col span={24}>
                                        <Controller
                                          name='state'
                                          control={control}
                                          className='inputLabel'
                                          as={
                                            <Input
                                              size='large'
                                              disabled={disabledFormData}
                                            />
                                          }
                                        />
                                        {errors.state && (
                                          <p className='errorMessage'>
                                            {errors.state.message}
                                          </p>
                                        )}
                                      </Col>
                                    </Row>
                                  </Col>
                                </Row>
                              </Col>
                              <Col span={24}>
                                <Row gutter={[10, 16]}>
                                  <Col span={12}>
                                    <Row gutter={[10, 16]}>
                                      <Col span={24}>
                                        <Typography
                                          className='formHeading'
                                          style={{ marginBottom: '-15px' }}
                                        >
                                          Country
                                        </Typography>
                                      </Col>
                                      <Col span={24}>
                                        <Controller
                                          name='country'
                                          control={control}
                                          style={{
                                            width: '100%',
                                            fontFamily: 'Circular-900',
                                          }}
                                          defaultValue='IN'
                                          as={<Select size='large' disabled />}
                                        />
                                      </Col>
                                    </Row>
                                  </Col>
                                  <Col span={12}>
                                    <Row gutter={[10, 16]}>
                                      <Col span={24}>
                                        <Typography
                                          className='formHeading'
                                          style={{ marginBottom: '-15px' }}
                                        >
                                          Postal code
                                        </Typography>
                                      </Col>
                                      <Col span={24}>
                                        <Controller
                                          name='postal_code'
                                          control={control}
                                          className='inputLabel'
                                          as={
                                            <Input
                                              size='large'
                                              disabled={disabledFormData}
                                            />
                                          }
                                        />
                                        {errors.postal_code && (
                                          <p className='errorMessage'>
                                            {errors.postal_code.message}
                                          </p>
                                        )}
                                      </Col>
                                    </Row>
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                          </Col>
                          <Col>
                            <Typography
                              className='formHeading'
                              style={{ paddingBottom: '10px' }}
                            >
                              {' '}
                              Operational Address{' '}
                            </Typography>
                            <Checkbox
                              onChange={(e) =>
                                setSameAsRegistered(e.target.checked)
                              }
                              style={{ marginBottom: '10px' }}
                            >
                              {' '}
                              <Typography className='formHeading'>
                                Same as Registered Address
                              </Typography>
                            </Checkbox>
                            <Row
                              gutter={[5, 16]}
                              style={{
                                border: '1px solid #ccc',
                                padding: '10px',
                              }}
                            >
                              <Col span={24}>
                                <Row gutter={[10, 16]}>
                                  <Col span={24}>
                                    <Typography
                                      className='formHeading'
                                      style={{ marginBottom: '-15px' }}
                                    >
                                      Address line 1
                                    </Typography>
                                  </Col>
                                  <Col span={24}>
                                    <Controller
                                      name='operationstreet1'
                                      control={control}
                                      className='inputLabel'
                                      as={
                                        <Input
                                          size='large'
                                          disabled={disabledFormData}
                                        />
                                      }
                                    />
                                    {errors.operationstreet1 && (
                                      <p className='errorMessage'>
                                        {errors.operationstreet1.message}
                                      </p>
                                    )}
                                  </Col>
                                </Row>
                              </Col>
                              <Col span={24}>
                                <Row gutter={[10, 16]}>
                                  <Col span={24}>
                                    <Typography
                                      className='formHeading'
                                      style={{ marginBottom: '-15px' }}
                                    >
                                      Address line 2
                                    </Typography>
                                  </Col>
                                  <Col span={24}>
                                    <Controller
                                      name='operationstreet2'
                                      control={control}
                                      className='inputLabel'
                                      as={
                                        <Input
                                          size='large'
                                          disabled={disabledFormData}
                                        />
                                      }
                                    />
                                    {errors.operationstreet2 && (
                                      <p className='errorMessage'>
                                        {errors.operationstreet2.message}
                                      </p>
                                    )}
                                  </Col>
                                </Row>
                              </Col>
                              <Col span={24}>
                                <Row gutter={[10, 16]}>
                                  <Col span={12}>
                                    <Row gutter={[10, 16]}>
                                      <Col span={24}>
                                        <Typography
                                          className='formHeading'
                                          style={{ marginBottom: '-15px' }}
                                        >
                                          City
                                        </Typography>
                                      </Col>
                                      <Col span={24}>
                                        <Controller
                                          name='operationcity'
                                          control={control}
                                          className='inputLabel'
                                          as={
                                            <Input
                                              size='large'
                                              disabled={disabledFormData}
                                            />
                                          }
                                        />
                                        {errors.operationcity && (
                                          <p className='errorMessage'>
                                            {errors.operationcity.message}
                                          </p>
                                        )}
                                      </Col>
                                    </Row>
                                  </Col>
                                  <Col span={12}>
                                    <Row gutter={[10, 16]}>
                                      <Col span={24}>
                                        <Typography
                                          className='formHeading'
                                          style={{ marginBottom: '-15px' }}
                                        >
                                          State
                                        </Typography>
                                      </Col>
                                      <Col span={24}>
                                        <Controller
                                          name='operationstate'
                                          control={control}
                                          className='inputLabel'
                                          as={
                                            <Input
                                              size='large'
                                              disabled={disabledFormData}
                                            />
                                          }
                                        />
                                        {errors.operationstate && (
                                          <p className='errorMessage'>
                                            {errors.operationstate.message}
                                          </p>
                                        )}
                                      </Col>
                                    </Row>
                                  </Col>
                                </Row>
                              </Col>
                              <Col span={24}>
                                <Row gutter={[10, 16]}>
                                  <Col span={12}>
                                    <Row gutter={[10, 16]}>
                                      <Col span={24}>
                                        <Typography
                                          className='formHeading'
                                          style={{ marginBottom: '-15px' }}
                                        >
                                          Country
                                        </Typography>
                                      </Col>
                                      <Col span={24}>
                                        <Controller
                                          name='operationcountry'
                                          control={control}
                                          style={{
                                            width: '100%',
                                            fontFamily: 'Circular-900',
                                          }}
                                          defaultValue='IN'
                                          as={<Select size='large' disabled />}
                                        />
                                      </Col>
                                    </Row>
                                  </Col>
                                  <Col span={12}>
                                    <Row gutter={[10, 16]}>
                                      <Col span={24}>
                                        <Typography
                                          className='formHeading'
                                          style={{ marginBottom: '-15px' }}
                                        >
                                          Postal code
                                        </Typography>
                                      </Col>
                                      <Col span={24}>
                                        <Controller
                                          name='operationpostalcode'
                                          control={control}
                                          className='inputLabel'
                                          as={
                                            <Input
                                              size='large'
                                              disabled={disabledFormData}
                                            />
                                          }
                                        />
                                        {errors.operationpostalcode && (
                                          <p className='errorMessage'>
                                            {errors.operationpostalcode.message}
                                          </p>
                                        )}
                                      </Col>
                                    </Row>
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </Panel>
                    </Collapse>
                  </Col>
                  {selectedBusinessType !== 'individual' &&
                    selectedBusinessType !== 'not_yet_registered' && (
                      <Col span={24}>
                        <Row>
                          <Col span={24}>
                            <Typography className='formHeading'>
                              Legal Information
                            </Typography>
                          </Col>
                          <Col
                            span={24}
                            style={{
                              border: '1px solid #ccc',
                              padding: '10px',
                            }}
                          >
                            <Row gutter={[10, 16]}>
                              <Col span={12}>
                                <Row gutter={[10, 16]}>
                                  <Col span={24}>
                                    <Typography
                                      className='formHeading'
                                      style={{ marginBottom: '-15px' }}
                                    >
                                      PAN number
                                    </Typography>
                                  </Col>
                                  <Col span={24}>
                                    <Controller
                                      name='pan'
                                      control={control}
                                      className='inputLabel'
                                      as={
                                        <Input
                                          size='large'
                                          disabled={disabledFormData}
                                        />
                                      }
                                    />
                                    {/* {errors.pan && (
                                    <p className='errorMessage'>
                                      {errors.pan.message}
                                    </p>
                                  )} */}
                                  </Col>
                                </Row>
                              </Col>
                              <Col span={12}>
                                <Row gutter={[10, 16]}>
                                  <Col span={24}>
                                    <Typography
                                      className='formHeading'
                                      style={{ marginBottom: '-15px' }}
                                    >
                                      GST number
                                    </Typography>
                                  </Col>
                                  <Col span={24}>
                                    <Controller
                                      name='gst'
                                      control={control}
                                      className='inputLabel'
                                      as={
                                        <Input
                                          size='large'
                                          disabled={disabledFormData}
                                        />
                                      }
                                    />
                                    {/* {errors.gst && (
                          <p className='errorMessage'>{errors.gst.message}</p>
                        )} */}
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </Col>
                    )}
                </Row>
              </Col>
            </Row>
            <Row>
              <Col span={24} style={{ marginTop: '25px' }}>
                <Row gutter={[16, 16]} className='button' justify='center'>
                  <Col xl={6} lg={6} md={8} sm={12} xs={12}>
                    <Button
                      className={
                        disabledFormData ? 'classButton' : 'detailsButton'
                      }
                      htmlType='submit'
                      size='large'
                      disabled={disabledFormData}
                    >
                      Create
                    </Button>
                  </Col>
                  <Col xl={6} lg={6} md={8} sm={12} xs={12}>
                    <Button
                      size='large'
                      className='detailsButton'
                      onClick={next}
                    >
                      Next
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        )}
      </Spin>
    </form>
  );
};

export default LinkedAccount;
